import { useCallback } from 'react';
import {
  useQuery,
  useQueryClient,
  QueryFunctionContext,
} from '@tanstack/react-query';
import { getCompanyFeed } from '@simplywallst/services';
import { getCompanyFeedItems } from '@components/Dashboard/hooks/useFeed/selector';
import { formatterCurrency } from '@/utilities/format';

export async function queryFn(
  context: QueryFunctionContext<[string, string] | [string, string, number]>
) {
  const { 1: companyId, 2: limit } = context.queryKey;
  const response = await getCompanyFeed({ lookup: companyId, limit });
  return response.data.data;
}

export function getQueryKey(
  lookup: string,
  limit = 0
): [string, string] | [string, string, number] {
  if (limit) {
    return ['COMPANY_FEED', lookup, limit];
  }
  return ['COMPANY_FEED', lookup];
}

export interface Options {
  limit?: number;
  types?: ('article' | 'external-article' | 'brief' | 'event' | 'narrative')[];
}

export default function useCompanyFeed(
  companyId: string | null,
  options?: Options
) {
  const queryClient = useQueryClient();
  const { isLoading, data } = useQuery({
    queryKey: getQueryKey(companyId as string, options?.limit),
    queryFn,
    initialData: () => {
      // Reuse cache from watchlist/dashboard feed if available
      const existingData = queryClient.getQueryData<
        Awaited<ReturnType<typeof queryFn>>
      >(getQueryKey(companyId as string));
      return existingData;
    },
    retry: 1,
    staleTime: 3600000,
    refetchOnMount: false,
    enabled: Boolean(companyId),
    select: useCallback(
      (companyEvents: Awaited<ReturnType<typeof queryFn>>) => {
        try {
          let selectedEvents = getCompanyFeedItems(companyEvents.events.data, {
            types: options?.types,
            canonicalUrl: companyEvents.canonical_url,
          });
          if (typeof options?.limit === 'number') {
            selectedEvents = selectedEvents.slice(0, options.limit);
          }
          return {
            name: companyEvents.name,
            tickerSymbol: companyEvents.ticker_symbol,
            canonicalUrl: companyEvents.canonical_url,
            companyId: companyEvents.id,
            scores: companyEvents.snowflake.data.axes,
            return7D: companyEvents.return_7d,
            return1YrAbs: companyEvents.return_1yr_abs,
            sharePriceFormatted: formatterCurrency(
              companyEvents.last_share_price,
              companyEvents.currency_iso
            ),
            feedItems: selectedEvents,
          };
        } catch (e) {
          return undefined;
        }
      },
      [companyId, options?.limit]
    ),
  });

  return {
    data,
    isLoading,
  };
}
